import { useState } from 'react'
import { Button } from '../../design-system/primitives/index.js'
import StateBanner from '../StateBanner/StateBanner.jsx'
import Login from '../Login.jsx'
import { HOME_COPY, returningMemoryLine } from '../../config/homeCopy.js'
import HowItWorks from './HowItWorks.jsx'

/** Reserved hero stage for the loop demo (animation/video). Aspect ratio locked for layout. */
function DemoStage() {
  const { label, placeholder, hint, beats } = HOME_COPY.demo
  return (
    <aside className="guest-home__demo" aria-label={label}>
      <p className="guest-home__card-label">{label}</p>
      <div className="guest-home__demo-stage">
        <div className="guest-home__demo-poster">
          <span className="guest-home__demo-play" aria-hidden="true">
            <i className="fas fa-play" />
          </span>
          <p className="guest-home__demo-placeholder">{placeholder}</p>
          <p className="guest-home__demo-hint">{hint}</p>
        </div>
      </div>
      <ol className="guest-home__demo-beats" aria-label="Story beats">
        {beats.map(([number, title]) => (
          <li key={number}>
            <span aria-hidden="true">{number}</span>
            {title}
          </li>
        ))}
      </ol>
    </aside>
  )
}

/** Demoted benefit strip — keeps outcomes visible without competing with the demo. */
function BenefitStrip() {
  return (
    <section className="guest-home__benefits" aria-labelledby="guest-benefits-title">
      <p id="guest-benefits-title" className="guest-home__eyebrow">{HOME_COPY.benefitsHeading}</p>
      <ul className="guest-home__benefit-strip">
        {HOME_COPY.benefits.map(([title, body]) => (
          <li key={title}>
            <i className="fas fa-check" aria-hidden="true" />
            <span>
              <strong>{title}</strong>
              <small>{body}</small>
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function AssuranceRow() {
  return (
    <section className="guest-home__assurances" aria-label="Why people start here">
      {HOME_COPY.assurances.map(([icon, title, body]) => (
        <article key={title}>
          <i className={`fas ${icon}`} aria-hidden="true" />
          <strong>{title}</strong>
          <p>{body}</p>
        </article>
      ))}
    </section>
  )
}

function StartActions({ startStatus, onStart, onLogin, actionLabel = 'Start my kitchen' }) {
  if (startStatus === 'error') {
    return (
      <StateBanner
        variant="urgent"
        headline="Couldn’t start your kitchen"
        body="Your account options are still available, and nothing was changed."
        action={{ label: 'Try again', onClick: onStart }}
        secondaryAction={{ label: 'Log in instead', onClick: onLogin }}
        role="alert"
      />
    )
  }
  return (
    <div className="guest-home__actions">
      <Button variant="primary" onClick={onStart} disabled={startStatus === 'busy'}>
        {startStatus === 'busy' ? 'Starting kitchen…' : actionLabel}
      </Button>
      <Button variant="secondary" onClick={onLogin}>Log in</Button>
    </div>
  )
}

function NewbieHome({ startStatus, onStart, onLogin, onHow, actionLabel }) {
  return (
    <>
      <section className="guest-home__hero">
        <div className="guest-home__hero-copy">
          <p className="guest-home__eyebrow">{HOME_COPY.newbie.eyebrow}</p>
          <h1>{HOME_COPY.newbie.title}</h1>
          <p className="guest-home__lead">{HOME_COPY.newbie.body}</p>
          <StartActions startStatus={startStatus} onStart={onStart} onLogin={onLogin} actionLabel={actionLabel} />
          <p className="guest-home__trust">{HOME_COPY.newbie.trust}</p>
          {startStatus === 'error' ? null : (
            <div className="guest-home__how">
              <Button variant="link" onClick={onHow}>See how it works</Button>
            </div>
          )}
        </div>
        <DemoStage />
      </section>
      <BenefitStrip />
      <AssuranceRow />
    </>
  )
}

function ReturningHome({ snapshot, onStart, onLoginSuccess }) {
  const firstName = snapshot?.firstName?.trim()
  const memory = returningMemoryLine(snapshot)
  return (
    <section className="guest-home__returning">
      <h1>{firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}</h1>
      <Login embedded idPrefix="returning-login" onLoginSuccess={onLoginSuccess} />
      {memory ? <p className="guest-home__memory">{memory}</p> : null}
      <div className="guest-home__returning-exit">
        <Button variant="link" onClick={onStart}>Not you? Start your own kitchen</Button>
      </div>
    </section>
  )
}

function ExpiredHome({ startStatus, onStart, onLogin }) {
  return (
    <section className="guest-home__expired">
      <span className="guest-home__expired-icon" aria-hidden="true">
        <i className="fas fa-clock" />
      </span>
      <p className="guest-home__eyebrow">A FRESH START</p>
      <h1>{HOME_COPY.expired.title}</h1>
      <p className="guest-home__lead">{HOME_COPY.expired.body}</p>
      <div className="guest-home__actions">
        <Button variant="primary" onClick={onStart} disabled={startStatus === 'busy'}>
          {startStatus === 'busy' ? 'Starting again…' : 'Start again'}
        </Button>
        <Button variant="secondary" onClick={onLogin}>Log in</Button>
      </div>
    </section>
  )
}

export default function HomePage({
  cohort,
  snapshot,
  startStatus,
  onStart,
  onLogin,
  onLoginSuccess,
  actionLabel = 'Start my kitchen',
  mobile = false,
}) {
  const [showHow, setShowHow] = useState(false)
  const startFromHow = async () => {
    setShowHow(false)
    await onStart()
  }

  return (
    <div className="guest-home">
      {cohort === 'returning' ? (
        <ReturningHome snapshot={snapshot} onStart={onStart} onLoginSuccess={onLoginSuccess} />
      ) : cohort === 'expired' ? (
        <ExpiredHome startStatus={startStatus} onStart={onStart} onLogin={onLogin} />
      ) : (
        <NewbieHome
          startStatus={startStatus}
          onStart={onStart}
          onLogin={onLogin}
          onHow={() => setShowHow(true)}
          actionLabel={actionLabel}
        />
      )}
      <HowItWorks
        open={showHow}
        onClose={() => setShowHow(false)}
        onStart={startFromHow}
        actionLabel={actionLabel}
        mobile={mobile}
      />
    </div>
  )
}
